/**
 * Registry and audience rules for the "what's new" announcements.
 *
 * Deliberately free of React: deciding *who* gets told about a feature is the part worth testing on
 * its own, and it is also the part that is easy to get wrong in a way nobody notices — a leak shows
 * up as a public visitor reading about a button they cannot see, which no test of the markup would
 * catch. The copy lives next door in `index.translations.ts`, keyed off each announcement's id.
 *
 * To announce a new feature: add an entry to `FEATURE_ANNOUNCEMENTS` and the four `<id>*` keys it
 * names to the translations file. Nothing else has to change.
 */

/** The roles the database can hold, mirroring `userRoleEnum` in the schema. */
export const USER_ROLES = [
  "author",
  "verified_author",
  "moderator",
  "admin",
  "owner",
] as const;

export type UserRole = (typeof USER_ROLES)[number];

/**
 * Who a visitor is, as far as announcements are concerned: their role, or one of the two states
 * that are not roles — signed out, and signed in without a role granted yet.
 */
export type Audience = "guest" | "member" | UserRole;

/**
 * Everyone who can reach the Add video modal. Mirrors the role gate in the videos router
 * (`videos.create`) rather than the shorter list in the account sidebar, because the router is what
 * actually decides whether an upload succeeds.
 */
export const UPLOADERS = [
  "author",
  "verified_author",
  "moderator",
  "admin",
  "owner",
] as const satisfies readonly Audience[];

/** Roles that moderate other people's work. */
export const STAFF = ["moderator", "admin", "owner"] as const satisfies readonly Audience[];

/** Every signed-in visitor, whatever role they hold. */
export const SIGNED_IN = ["member", ...USER_ROLES] as const satisfies readonly Audience[];

/** Everyone, signed in or not. */
export const EVERYONE = ["guest", ...SIGNED_IN] as const satisfies readonly Audience[];

export interface FeatureAnnouncement {
  /**
   * Stable identifier. Doubles as the dismissal key stored in the browser and as the prefix of the
   * announcement's copy keys, so renaming one retires it and resurfaces it for everybody.
   */
  readonly id: string;
  /** ISO date the feature shipped. Orders the list, and drives retirement via `maxAgeDays`. */
  readonly releasedAt: string;
  /**
   * Every audience that should be told, listed explicitly. A rank ("author and above") would be
   * shorter but would quietly widen an announcement the day a new role is inserted into the order.
   */
  readonly audiences: readonly Audience[];
  /**
   * Where the feature lives, relative to the locale root — the component turns it into
   * `/{locale}{path}`. Left out when the announcement has nowhere useful to send the reader.
   */
  readonly path?: string;
}

/**
 * Newest first is not required — `selectAnnouncements` sorts — but keeping the newest at the top
 * makes the list read the way it renders.
 */
export const FEATURE_ANNOUNCEMENTS: readonly FeatureAnnouncement[] = [
  {
    id: "dafSuggestion",
    releasedAt: "2026-08-28",
    audiences: UPLOADERS,
    path: "/account/videos",
  },
  {
    id: "titleFromFile",
    releasedAt: "2026-08-28",
    audiences: UPLOADERS,
    path: "/account/videos",
  },
];

/** How long an announcement stays "new" before it retires on its own. */
export const DEFAULT_MAX_AGE_DAYS = 60;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Maps a visitor onto an audience. An unrecognised role — one added to the database but not yet to
 * `USER_ROLES` — falls back to `member` rather than to itself, so a half-deployed role can only
 * ever see less than it should, never more.
 */
export function resolveAudience({
  isSignedIn,
  role,
}: {
  isSignedIn: boolean;
  role?: string | null;
}): Audience {
  if (!isSignedIn) return "guest";
  return (USER_ROLES as readonly string[]).includes(role ?? "")
    ? (role as UserRole)
    : "member";
}

export interface SelectAnnouncementsOptions {
  readonly audience: Audience;
  /** Ids the visitor has already been shown. */
  readonly seenIds?: readonly string[];
  /** Overrides `DEFAULT_MAX_AGE_DAYS`; pass `Infinity` to keep announcements indefinitely. */
  readonly maxAgeDays?: number;
  /** Injectable clock, so the age rule is testable. */
  readonly now?: Date;
  /** Caps how many are shown at once. */
  readonly limit?: number;
  /** The registry to read from. Defaults to the shipped one; overridden by tests. */
  readonly announcements?: readonly FeatureAnnouncement[];
}

/**
 * The announcements a visitor should be offered right now: addressed to them, not already seen,
 * not stale — newest first.
 */
export function selectAnnouncements({
  audience,
  seenIds = [],
  maxAgeDays = DEFAULT_MAX_AGE_DAYS,
  now = new Date(),
  limit,
  announcements = FEATURE_ANNOUNCEMENTS,
}: SelectAnnouncementsOptions): FeatureAnnouncement[] {
  const alreadySeen = new Set(seenIds);

  const visible = announcements
    .filter((announcement) => announcement.audiences.includes(audience))
    .filter((announcement) => !alreadySeen.has(announcement.id))
    .filter((announcement) => {
      const released = new Date(announcement.releasedAt).getTime();
      // An unparseable date would otherwise make the announcement immortal; drop it instead.
      if (Number.isNaN(released)) return false;
      if (maxAgeDays === Infinity) return true;
      return now.getTime() - released <= maxAgeDays * MS_PER_DAY;
    })
    .sort(
      (a, b) => new Date(b.releasedAt).getTime() - new Date(a.releasedAt).getTime(),
    );

  return limit === undefined ? visible : visible.slice(0, limit);
}
